import { useTranslation } from "@sagot/i18n";
import type { PresenceStatus } from "@sagot/constants";
import {
  Button,
  Chip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  toast,
} from "@sagot/ui";
import { usePermissions } from "../../../session/permissions";
import { PRESENCE_CHOICES, PRESENCE_META, statusLabelKey } from "./presence-model";
import { isLiveChatOff, useLiveChatState, useMyPresence, usePresenceHeartbeat, useSetMyPresence } from "./use-presence";

function isStatus(value: string): value is PresenceStatus {
  return PRESENCE_CHOICES.some((status) => status === value);
}

/**
 * Header status picker (M29 HND-003, inbox pin 1): online / away / busy / offline plus "Chats 2 / 3". Only members who can reply
 * to chats see it, and only where the `live_chat_ops` flag is on: the rollout probe is asked first, so a workspace outside it never
 * requests a flagged route (no 404 in the console). The console keeps the member
 * online with a heartbeat while they work; five minutes of silence reads as Away until their next move.
 */
export function StatusPicker() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canChat = permissions.can("inbox.reply", { anywhere: true }) && !permissions.readOnly;
  const state = useLiveChatState(canChat);
  const on = state.data?.enabled === true;
  const me = useMyPresence(canChat && on);
  const set = useSetMyPresence();
  usePresenceHeartbeat(me.data, (status) => { set.mutate(status); });

  if (!canChat || !on) return null;
  if (me.isPending) return <Skeleton className="h-9 w-24" data-testid="presence-loading" role="status" aria-label={t("routingLive.presence.loading")} />;
  if (me.isError) {
    if (isLiveChatOff(me.error)) return null;
    return (
      <Button variant="ghost" size="sm" onClick={() => { void me.refetch(); }} data-testid="presence-error">
        <Icon name="alert-triangle" />
        {t("routingLive.presence.loadFailed")}
      </Button>
    );
  }

  const row = me.data;
  const meta = PRESENCE_META[row.status];
  const statusLabel = t(statusLabelKey(row.status, row.auto_away));
  const atLimit = row.active_chats >= row.chat_limit;
  const pick = (value: string) => {
    if (!isStatus(value)) return;
    set.mutate(value, {
      onSuccess: () => { toast.success(t("routingLive.presence.changed", { status: t(PRESENCE_META[value].label) })); },
      onError: () => { toast.error(t("routingLive.presence.changeFailed")); },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 pointer-coarse:min-h-11"
          data-testid="presence-trigger"
          aria-label={t("routingLive.presence.trigger", { status: statusLabel, count: row.active_chats, limit: row.chat_limit })}
        >
          <span aria-hidden="true" className={`size-2 shrink-0 rounded-pill ${meta.dot}`} />
          <span className="max-w-28 truncate" data-testid="presence-label">{statusLabel}</span>
          <span className="hidden md:inline-flex">
            <Chip tone={atLimit ? "warning" : "muted"} data-testid="presence-chats">
              {t("routingLive.presence.chats", { count: row.active_chats, limit: row.chat_limit })}
            </Chip>
          </span>
          <Icon name="chevron-down" className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>{t("routingLive.presence.menu")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={row.status} onValueChange={pick}>
          {PRESENCE_CHOICES.map((status) => (
            <DropdownMenuRadioItem key={status} value={status} className="items-start gap-3 py-2" data-testid={`presence-option-${status}`}>
              <Icon name={PRESENCE_META[status].icon} className="mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{t(PRESENCE_META[status].label)}</span>
                <span className="block text-xs text-fg-muted">{t(PRESENCE_META[status].hint)}</span>
              </span>
              <DropdownMenuItemIndicator>
                <Icon name="check-circle" className="size-4 text-primary-ink" />
              </DropdownMenuItemIndicator>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuLabel className="mt-1 space-y-1 border-t border-line pt-2 text-xs font-normal normal-case tracking-normal text-fg-muted">
          <span className={atLimit ? "block font-semibold text-warning" : "block"}>
            {atLimit ? t("routingLive.presence.atLimit") : t("routingLive.presence.chats", { count: row.active_chats, limit: row.chat_limit })}
          </span>
          {row.auto_away ? <span className="block" data-testid="presence-auto-away">{t("routingLive.presence.autoAwayNotice")}</span> : null}
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
